import { Plus, WarningCircle, X } from '@phosphor-icons/react';
import { usePreviewMediaCollection } from '@reelplicate/api-client/media-collections';
import type { MediaCollectionPreviewDto } from '@reelplicate/api-client/model';
import {
  Button,
  cn,
  Field,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Modal,
  ModalClose,
  ModalContent,
  MultiSelect,
  Select,
  SelectItem,
  Stack,
  TextInput,
  Toggle,
} from '@reelplicate/ui';
import { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { Facet } from './library';
import { useMedia } from './media-context';
import type { Collection, FilterState, SmartRule, SmartSort } from './types';

type Condition = { facet: string; values: string[] };

/** What opens the builder: a new rule (maybe prefilled), or an existing collection's. */
export type RuleBuilderTarget = {
  collection?: Collection;
  initial?: { name?: string; rule?: SmartRule };
};

const SORTS: { value: SmartSort; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'views', label: 'Most views' },
  { value: 'lift', label: 'Most above their usual' },
  { value: 'posted', label: 'Latest posted' },
];
const KEEP = ['all', '10', '20', '50', '100'] as const;

/** How a condition reads between its facet and its values. */
const OPERATOR: Record<string, string> = {
  performance: 'has',
  added: 'added in',
  lift: 'by',
  winner: 'became one',
  posted: 'was posted',
  tags: 'has',
};

const conditionsOf = (filters: FilterState): Condition[] =>
  Object.entries(filters)
    .filter(([, values]) => values.length > 0)
    .map(([facet, values]) => ({ facet, values }));

/**
 * Figma "Modal · Smart collection" (482:3498): name a smart collection and
 * say what it holds - conditions that must all match, words it mentions, an
 * order and a cap - with a live count of what matches as you go. Creates a
 * collection, or saves an existing one's rules.
 */
export function RuleBuilderModal({
  target,
  onClose,
}: {
  target: RuleBuilderTarget | null;
  onClose: () => void;
}) {
  return (
    <Modal open={target !== null} onOpenChange={(open) => !open && onClose()}>
      {target && (
        <Builder key={target.collection?.id ?? 'new'} target={target} onClose={onClose} />
      )}
    </Modal>
  );
}

function Builder({
  target,
  onClose,
}: {
  target: RuleBuilderTarget;
  onClose: () => void;
}) {
  const {
    ruleFacets: facets,
    workspaceId,
    createCollection,
    updateCollection,
  } = useMedia();
  const navigate = useNavigate();
  const editing = target.collection;
  const start = editing?.smart ?? target.initial?.rule;
  const [name, setName] = useState(editing?.name ?? target.initial?.name ?? '');
  const [conditions, setConditions] = useState<Condition[]>(() =>
    conditionsOf(start?.filters ?? {}),
  );
  const [query, setQuery] = useState(start?.query ?? '');
  const [sort, setSort] = useState<SmartSort>(start?.sort ?? 'newest');
  const [keep, setKeep] = useState<string>(start?.limit ? String(start.limit) : 'all');
  const [feed, setFeed] = useState(start?.includeCompetitorFeed ?? false);
  const feedId = useId();
  const [saving, setSaving] = useState(false);
  const broken = new Set(editing?.smart?.broken.map((b) => `${b.facet}:${b.value}`));

  const rule: SmartRule = useMemo(
    () => ({
      filters: Object.fromEntries(
        conditions.filter((c) => c.values.length).map((c) => [c.facet, c.values]),
      ),
      query: query.trim() || undefined,
      sort,
      limit: keep === 'all' ? null : Number(keep),
      includeCompetitorFeed: feed,
    }),
    [conditions, query, sort, keep, feed],
  );
  const preview = usePreview(workspaceId, rule);

  const unused = facets.filter((f) => !conditions.some((c) => c.facet === f.id));
  const update = (index: number, next: Partial<Condition>) =>
    setConditions((all) => all.map((c, i) => (i === index ? { ...c, ...next } : c)));

  const save = async () => {
    setSaving(true);
    const saved = editing
      ? await updateCollection(editing.id, { name: name.trim(), rule })
      : await createCollection(name.trim(), { rule });
    setSaving(false);
    if (!saved) return;
    onClose();
    if (!editing) void navigate(`/media/collections/${saved.id}`);
  };

  return (
    <ModalContent
      size="lg"
      title={editing ? 'Edit rules' : 'New smart collection'}
      description={
        editing
          ? `${editing.name} · ${editing.count} ${editing.count === 1 ? 'item' : 'items'} today. Changes apply everywhere it’s used, flows included.`
          : 'It fills itself: anything in your library that matches these conditions joins, now and later.'
      }
      footer={
        <>
          <p className="text-caption text-text-tertiary mr-auto">
            Items can’t be added by hand. Save a hand-picked copy any time.
          </p>
          <ModalClose asChild>
            <Button variant="ghost">Cancel</Button>
          </ModalClose>
          <Button disabled={!name.trim()} loading={saving} onClick={() => void save()}>
            {editing ? 'Save rules' : 'Create collection'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void save();
        }}
      >
        <Stack gap={5}>
          <Field label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Top performers"
              autoComplete="off"
            />
          </Field>

          <Stack gap={2}>
            <p className="text-label-md text-text-primary">
              Include items that match{' '}
              <span className="text-caption text-text-tertiary">
                all of these conditions
              </span>
            </p>
            {conditions.length === 0 && (
              <p className="text-body-sm text-text-secondary">
                No conditions yet: it matches your whole library.
              </p>
            )}
            <ul aria-label="Conditions" className="flex flex-col gap-2">
              {conditions.map((condition, i) => {
                const facet = facets.find((f) => f.id === condition.facet);
                if (!facet) return null;
                const isBroken = condition.values.some((v) =>
                  broken.has(`${condition.facet}:${v}`),
                );
                return (
                  <ConditionRow
                    key={condition.facet}
                    facet={facet}
                    choices={[facet, ...unused]}
                    values={condition.values}
                    broken={isBroken}
                    onFacetChange={(facetId) => update(i, { facet: facetId, values: [] })}
                    onValuesChange={(values) => update(i, { values })}
                    onRemove={() =>
                      setConditions((all) => all.filter((_, index) => index !== i))
                    }
                  />
                );
              })}
            </ul>
            {unused.length > 0 && (
              <Menu>
                <MenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={Plus}
                    className="self-start"
                  >
                    Add condition
                  </Button>
                </MenuTrigger>
                <MenuContent align="start" aria-label="Add a condition">
                  {unused.map((facet) => (
                    <MenuItem
                      key={facet.id}
                      onSelect={() =>
                        setConditions((all) => [...all, { facet: facet.id, values: [] }])
                      }
                    >
                      {facet.label}
                    </MenuItem>
                  ))}
                </MenuContent>
              </Menu>
            )}
          </Stack>

          <Field label="Mentions" optional>
            <TextInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Words in its tags, title, caption or transcript"
              autoComplete="off"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Sort by">
              <Select value={sort} onValueChange={(v) => setSort(v as SmartSort)}>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </Select>
            </Field>
            <Field label="Keep">
              <Select value={keep} onValueChange={setKeep}>
                {KEEP.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k === 'all' ? 'Everything that matches' : `Top ${k}`}
                  </SelectItem>
                ))}
              </Select>
            </Field>
          </div>

          <div className="flex items-start gap-2.5">
            <Toggle
              id={feedId}
              checked={feed}
              onCheckedChange={setFeed}
              aria-describedby={`${feedId}-hint`}
            />
            <label htmlFor={feedId} className="flex flex-col gap-0.5">
              <span className="text-label-sm text-text-primary">
                Also match competitors’ posts not saved yet
              </span>
              <span id={`${feedId}-hint`} className="text-caption text-text-secondary">
                From the accounts you track. A flow that uses one saves it to your library
                first.
              </span>
            </label>
          </div>

          <Preview preview={preview} />
        </Stack>
      </form>
    </ModalContent>
  );
}

function ConditionRow({
  facet,
  choices,
  values,
  broken,
  onFacetChange,
  onValuesChange,
  onRemove,
}: {
  facet: Facet;
  choices: Facet[];
  values: string[];
  broken: boolean;
  onFacetChange: (facetId: string) => void;
  onValuesChange: (values: string[]) => void;
  onRemove: () => void;
}) {
  const id = useId();
  // Values the facet no longer offers (a deleted persona) stay visible so they can be removed.
  const options = [
    ...facet.options
      .filter(
        (o) =>
          o.value !== 'published' || facet.id !== 'source' || values.includes(o.value),
      )
      .map((o) => ({ value: o.value, label: o.label })),
    ...values
      .filter((v) => !facet.options.some((o) => o.value === v))
      .map((v) => ({
        value: v,
        label:
          facet.id === 'account'
            ? 'Deleted persona'
            : facet.id === 'competitor'
              ? 'Removed competitor'
              : v,
      })),
  ];
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-md border p-2',
        broken
          ? 'border-status-danger-border bg-status-danger-bg'
          : 'border-border-default',
      )}
    >
      <Select
        size="sm"
        value={facet.id}
        onValueChange={onFacetChange}
        aria-label="Condition"
        className="w-38 shrink-0"
      >
        {choices.map((f) => (
          <SelectItem key={f.id} value={f.id}>
            {f.label}
          </SelectItem>
        ))}
      </Select>
      <span id={id} className="text-body-sm text-text-secondary shrink-0">
        {OPERATOR[facet.id] ?? 'is'}
      </span>
      <div className="min-w-0 flex-1">
        {facet.multiple ? (
          <MultiSelect
            size="sm"
            options={options}
            value={values}
            onValueChange={onValuesChange}
            placeholder="Pick…"
            aria-label={`${facet.label} values`}
          />
        ) : (
          <Select
            size="sm"
            value={values[0] ?? ''}
            onValueChange={(v) => onValuesChange([v])}
            placeholder="Pick…"
            aria-label={`${facet.label} value`}
          >
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </Select>
        )}
      </div>
      {broken && (
        <WarningCircle
          aria-label="Points at something deleted"
          className="text-status-danger-fg size-4 shrink-0"
        />
      )}
      <IconButton
        icon={X}
        size="sm"
        variant="ghost"
        aria-label={`Remove ${facet.label} condition`}
        onClick={onRemove}
      />
    </li>
  );
}

/** The live count, asked for a moment after the rule stops changing. */
function usePreview(workspaceId: string, rule: SmartRule) {
  const { mutate } = usePreviewMediaCollection();
  const [preview, setPreview] = useState<MediaCollectionPreviewDto | null>(null);
  const key = JSON.stringify(rule);
  useEffect(() => {
    if (!workspaceId) return;
    const timer = setTimeout(() => {
      mutate(
        { id: workspaceId, data: { rule: JSON.parse(key) as SmartRule } },
        { onSuccess: setPreview },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [workspaceId, key, mutate]);
  return preview;
}

function Preview({ preview }: { preview: MediaCollectionPreviewDto | null }) {
  const thumbs = preview?.sample.flatMap((a) => (a.thumbnailUrl ? [a.thumbnailUrl] : []));
  return (
    <div
      role="status"
      className="bg-bg-subtle border-border-subtle flex items-center gap-3.5 rounded-md border px-4 py-3"
    >
      {thumbs && thumbs.length > 0 && (
        <span aria-hidden className="flex">
          {thumbs.map((url, i) => (
            <img
              key={url}
              src={url}
              alt=""
              className={cn(
                'border-bg-surface h-10 w-8 rounded-md border-2 object-cover',
                i > 0 && '-ml-2.5',
              )}
            />
          ))}
        </span>
      )}
      <span className="flex min-w-0 flex-col">
        <span className="text-body-sm-strong text-text-primary">
          {preview === null
            ? 'Counting…'
            : `${preview.count} ${preview.count === 1 ? 'item matches' : 'items match'} right now`}
        </span>
        <span className="text-caption text-text-secondary">
          New matches join on their own.
        </span>
      </span>
    </div>
  );
}
